"use client"

import { useEffect, useState } from "react"
import { X } from "lucide-react"
import { 
  updateBioBirthdate, 
  updateBioEducation, 
  updateBioLocation, 
  updateBioReligion, 
  updateBioPoliticalIdentities,
  PrivacyLevel,
  updateBioGender,
  updateBioAge
} from "util/userUtils"
import Modal from "ui/Modal"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from "ui/InputSelect"
import { 
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "ui/DropdownMenu"
import { toast } from "sonner"
import api from "config/axiosConfig"
import ActorPrivacy from "components/Common/ActorPrivacy"

// Define option types for the dropdown fields
interface OptionType {
  value: string;
  label: string;
}

// Religion options
const religionOptions: OptionType[] = [
  { value: "atheism", label: "Atheism" },
  { value: "agnosticism", label: "Agnosticism" },
  { value: "christianity", label: "Christianity" },
  { value: "islam", label: "Islam" },
  { value: "hinduism", label: "Hinduism" },
  { value: "buddhism", label: "Buddhism" },
  { value: "shinto", label: "Shinto" },
  { value: "taoism", label: "Taoism" },
  { value: "sikhism", label: "Sikhism" },
  { value: "judaism", label: "Judaism" },
  { value: "naturalism_religious", label: "Naturalism (religious)" },
  { value: "naturalism_non_religious", label: "Naturalism (non-religious)" },
  { value: "other", label: "Other" },
]

// Education options
const educationOptions: OptionType[] = [
  { value: "none", label: "None" },
  { value: "some_high_school", label: "Some high school" },
  { value: "high_school", label: "High school" },
  { value: "some_college", label: "Some college" },
  { value: "trade_school", label: "Trade school" },
  { value: "associates_degree", label: "Associate's degree" },
  { value: "bachelors_degree", label: "Bachelor's degree" },
  { value: "masters_degree", label: "Master's degree" },
  { value: "doctoral_degree", label: "Doctoral degree" },
  { value: "other", label: "Other" },
  { value: "rather_not_say", label: "Rather not say" },
]

// Gender options
const genderOptions: OptionType[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "rather_not_say", label: "Rather not say" },
]
// Political identity options
const politicalIdentityOptions: OptionType[] = [
  { value: "centrist", label: "Centrist" },
  { value: "moderate", label: "Moderate" },
  { value: "right-wing", label: "Right-wing" },
  { value: "left-wing", label: "Left-wing" },
  { value: "liberal", label: "Liberal" },
  { value: "neoliberal", label: "Neoliberal" },
  { value: "conservative", label: "Conservative" },
  { value: "progressive", label: "Progressive" },
  { value: "traditionalist", label: "Traditionalist" },
  { value: "libertarian", label: "Libertarian" },
  { value: "populist", label: "Populist" },
  { value: "nationalist", label: "Nationalist" },
  { value: "social_democrat", label: "Social Democrat" },
  { value: "communist", label: "Communist" },
  { value: "capitalist", label: "Capitalist" },
  { value: "anarchist", label: "Anarchist" },
  { value: "antifascist", label: "Antifascist" },
  { value: "fascist", label: "Fascist" },
  { value: "monarchist", label: "Monarchist" },
  { value: "environmentalist", label: "Environmentalist" },
  { value: "feminist", label: "Feminist" },
  { value: "internationalist", label: "Internationalist" },
  { value: "radical", label: "Radical" },
  { value: "marxist", label: "Marxist" },
  { value: "leninist", label: "Leninist" },
  { value: "corporatist", label: "Corporatist" },
  { value: "decentralist", label: "Decentralist" },
  { value: "technocrat", label: "Technocrat" },
  { value: "other", label: "Other" },
]

interface UserAboutBackgroundModalProps {
  isOpen: boolean;
  onClose: () => void;
  privacy?: PrivacyLevel;
  initialData?: {
    about_bio_birthdate?: {value: string; privacy: PrivacyLevel};
    about_bio_age?: {value: number; privacy: PrivacyLevel};
    about_bio_location?: {value: string; privacy: PrivacyLevel};
    about_bio_religion?: {value: string[]; privacy: PrivacyLevel};
    about_bio_education?: {value: string; privacy: PrivacyLevel};
    about_bio_political_identities?: {value: string[]; privacy: PrivacyLevel};
    about_bio_gender?: {value: string; privacy: PrivacyLevel};
  };
}

// Helper function to get all years from 1900 to current
const getYears = (): OptionType[] => {
  const currentYear = new Date().getFullYear();
  const years: OptionType[] = [];
  for (let year = currentYear - 13; year >= 1900; year--) {
    years.push({ value: year.toString(), label: year.toString() });
  }
  return years;
};

// Helper function to get all months
const getMonths = (): OptionType[] => {
  return [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];
};

// Helper function to get days for a specific month and year
const getDaysInMonth = (month: number, year: number): OptionType[] => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const days: OptionType[] = [];
  for (let day = 1; day <= daysInMonth; day++) {
    days.push({ value: day.toString(), label: day.toString() });
  }
  return days;
};

/**
 * Modal component for users to set their background information
 */
const UserAboutBackgroundModal: React.FC<UserAboutBackgroundModalProps> = ({
  isOpen,
  onClose,
  privacy = "public",
  initialData = {}
}) => {
  // State for the different form fields
  const [birthdate, setBirthdate] = useState<Date | undefined>(
    initialData.about_bio_birthdate?.value ? new Date(initialData.about_bio_birthdate.value) : undefined
  );
  
  const [birthYear, setBirthYear] = useState<string>(
    birthdate ? birthdate.getFullYear().toString() : ""
  );
  const [birthMonth, setBirthMonth] = useState<string>(
    birthdate ? (birthdate.getMonth() + 1).toString() : ""
  );
  const [birthDay, setBirthDay] = useState<string>(
    birthdate ? (birthdate.getDate() + 1).toString() : ""
  );
  const [daysInMonth, setDaysInMonth] = useState<OptionType[]>(
    getDaysInMonth(parseInt(birthMonth) || 1, parseInt(birthYear) || new Date().getFullYear())
  );
  const [gender, setGender] = useState<string>(initialData.about_bio_gender?.value || "");
  const [location, setLocation] = useState<string>(initialData.about_bio_location?.value || "");
  const [religion, setReligion] = useState<string[]>(
    Array.isArray(initialData.about_bio_religion?.value) ? initialData.about_bio_religion?.value : []
  );
  const [education, setEducation] = useState<string>(initialData.about_bio_education?.value || "");
  const [politicalIdentities, setPoliticalIdentities] = useState<string[]>(
    Array.isArray(initialData.about_bio_political_identities?.value) ? initialData.about_bio_political_identities?.value : []
  );
  const [countries, setCountries] = useState<OptionType[]>([]);
  // Fetch list of countries from the API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.get("/api/utilities/countries");
        if (response.data && Array.isArray(response.data)) {
          setCountries(
            response.data.map((country: any) => ({
              value: country.name,
              label: country.name
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        toast.error("Error", {
          description: "Failed to load countries list"
        });
      }
    };

    fetchCountries();
  }, []);

  // Update days when month or year changes
  useEffect(() => {
    if (birthMonth && birthYear) {
      setDaysInMonth(getDaysInMonth(parseInt(birthMonth), parseInt(birthYear)));
      
      // If the current day is beyond the days in the month, adjust it
      if (birthDay && parseInt(birthDay) > daysInMonth.length) {
        setBirthDay(daysInMonth.length.toString());
      }
    }
  }, [birthMonth, birthYear, birthDay, daysInMonth.length]);

  // Update initial state handling to avoid timezone issues
  useEffect(() => {
    if (initialData.about_bio_birthdate?.value) {
      const dateStr = initialData.about_bio_birthdate.value;
      const [year, month, day] = dateStr.split('-').map(part => parseInt(part));
      
      // Set state directly from parsed parts to avoid timezone issues
      setBirthYear(year.toString());
      setBirthMonth(month.toString());
      setBirthDay(day.toString());
      
      // Create date at noon to avoid timezone issues
      const date = new Date(year, month - 1, day, 12, 0, 0, 0);
      setBirthdate(date);
    }
  }, [initialData.about_bio_birthdate?.value]);

  // Handle date of birth change
  const updateBirthdate = async (newYear?: string, newMonth?: string, newDay?: string) => {
    // Use the passed values or fall back to state values
    const year = newYear || birthYear;
    const month = newMonth || birthMonth;
    const day = newDay || birthDay;
    
    if (year && month && day) {
      // Create date at noon to avoid timezone issues
      const newDate = new Date(
        parseInt(year),
        parseInt(month) - 1, // JavaScript months are 0-indexed
        parseInt(day),
        12, // Set to noon to avoid timezone issues
        0,
        0,
        0
      );
      
      setBirthdate(newDate);
      
      try {
        // Format date manually to avoid timezone issues
        const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        await updateBioBirthdate(isoDate, privacy);
        if (initialData.about_bio_birthdate) {
          initialData.about_bio_birthdate.value = isoDate;
        } else {
          initialData.about_bio_birthdate = {
            value: isoDate,
            privacy
          };
        }
        toast.success("Birthday Updated", {
          description: "Your birthday has been updated successfully."
        });
      } catch (error) {
        console.error("Error updating birthdate:", error);
        toast.error("Error", {
          description: "Failed to update your birthday"
        });
      }
    }
  };

  const handleYearChange = (value: string) => {
    setBirthYear(value);
    // Pass new values directly to updateBirthdate
    updateBirthdate(value, birthMonth, birthDay);
  };

  const handleMonthChange = (value: string) => {
    setBirthMonth(value);
    // Pass new values directly to updateBirthdate
    updateBirthdate(birthYear, value, birthDay);
  };

  const handleDayChange = (value: string) => {
    setBirthDay(value);
    // Pass new values directly to updateBirthdate
    updateBirthdate(birthYear, birthMonth, value);
  };

  const handleGenderChange = async (value: string) => {
    setGender(value);
    try {
      await updateBioGender(value, privacy);
      if (value) {
        initialData.about_bio_gender = {
          value: value,
          privacy: privacy
        }
      }
      toast.success("Gender Updated", {
        description: "Your gender has been updated successfully."
      });
    } catch (error) {
      console.error("Error updating gender:", error);
      toast.error("Error", {
        description: "Failed to update your gender"
      });
    }
  };

  // Handle location selection
  const handleLocationChange = async (value: string) => {
    setLocation(value);
    try {
      await updateBioLocation(value, privacy);
      if (value) {
        initialData.about_bio_location = {
          value: value,
          privacy: privacy
        }
      }
      toast.success("Location Updated", {
        description: "Your location has been updated successfully."
      });
    } catch (error) {
      console.error("Error updating location:", error);
      toast.error("Error", {
        description: "Failed to update your location"
      });
    }
  };

  // Handle religion selection/deselection
  const handleReligionToggle = async (value: string) => {
    let updatedReligions: string[];
    
    if (religion.includes(value)) {
      // Remove the religion if it's already selected
      updatedReligions = religion.filter(rel => rel !== value);
    } else {
      // Add the religion if it's not already selected
      updatedReligions = [...religion, value];
    }
    
    setReligion(updatedReligions);
    
    try {
      await updateBioReligion(updatedReligions, privacy);
      if (updatedReligions) {
        initialData.about_bio_religion = {
          value: updatedReligions,
          privacy: privacy
        }
      }
      toast.success("Religious Identities Updated", {
        description: "Your religious identities have been updated successfully."
      });
    } catch (error) {
      console.error("Error updating religion:", error);
      toast.error("Error", {
        description: "Failed to update your religious identities"
      });
    }
  };

  // Handle education selection
  const handleEducationChange = async (value: string) => {
    setEducation(value);
    try {
      await updateBioEducation(value, privacy);
      if (value) {
        initialData.about_bio_education = {
          value: value,
          privacy: privacy
        }
      }
      toast.success("Education Updated", {
        description: "Your education has been updated successfully."
      });
    } catch (error) {
      console.error("Error updating education:", error);
      toast.error("Error", {
        description: "Failed to update your education"
      });
    }
  };

  // Handle political identity selection/deselection
  const handlePoliticalIdentityToggle = async (value: string) => {
    let updatedIdentities: string[];
    
    if (politicalIdentities.includes(value)) {
      // Remove the identity if it's already selected
      updatedIdentities = politicalIdentities.filter(identity => identity !== value);
    } else {
      // Add the identity if it's not already selected
      updatedIdentities = [...politicalIdentities, value];
    }
    
    setPoliticalIdentities(updatedIdentities);
    
    try {
      await updateBioPoliticalIdentities(updatedIdentities, privacy);
      if (updatedIdentities) {
        initialData.about_bio_political_identities = {
          value: updatedIdentities,
          privacy: privacy
        }
      }
      toast.success("Political Identities Updated", {
        description: "Your political identities have been updated successfully."
      });
    } catch (error) {
      console.error("Error updating political identities:", error);
      toast.error("Error", {
        description: "Failed to update your political identities"
      });
    }
  };

  const getAge = () => {
    if (!birthdate) return "Unknown";
    const today = new Date();
    const birthDate = new Date(birthdate);
    let age = today.getFullYear() - birthDate.getFullYear();
    if (today < new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Add this handler function
  const handlePrivacyChange = (newPrivacy: PrivacyLevel, settingKey: string) => {
    // Update the initialData with the new privacy setting
    if (initialData[settingKey as keyof typeof initialData]) {
      const setting = initialData[settingKey as keyof typeof initialData];
      if (setting && typeof setting === 'object' && 'privacy' in setting) {
        (setting as any).privacy = newPrivacy;
      }
    }
    
    // Update the specific setting's privacy
    switch (settingKey) {
      case 'about_bio_birthdate':
        if (birthdate) {
          const isoDate = birthdate.toISOString().split('T')[0];
          updateBioBirthdate(isoDate, newPrivacy);
        }
        break;
      case 'about_bio_age':
        if (getAge()) {
          updateBioAge(0, newPrivacy);
        }
        break;
      case 'about_bio_location':
        if (location) {
          updateBioLocation(location, newPrivacy);
        }
        break;
      case 'about_bio_religion':
        if (religion.length > 0) {
          updateBioReligion(religion, newPrivacy);
        }
        break;
      case 'about_bio_education':
        if (education) {
          updateBioEducation(education, newPrivacy);
        }
        break;
      case 'about_bio_political_identities':
        if (politicalIdentities.length > 0) {
          updateBioPoliticalIdentities(politicalIdentities, newPrivacy);
        }
        break;
      case 'about_bio_gender':
        if (gender) {
          updateBioGender(gender, newPrivacy);
        }
        break;
    }
    
    toast.success("Privacy Updated", {
      description: "Your privacy settings have been updated successfully."
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Share your background"
      className="overflow-y-auto"
    >
      <div className="space-y-4">
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-meta">
          Tell others more about yourself so they better understand your perspective. All fields are optional and you can update your privacy settings for each field.
        </p>
        
        {/* Birthday */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium">Birthday (only visible to you)</label>
            <ActorPrivacy 
              privacy={'me'}
              settingKey="about_bio_birthdate"
              onPrivacyChange={() => {}}
              actorType="user"
              editMode={true}
              showDropdown={false}
              miniIcon={false}
              disabled={true}
              viewerRelation={'self'}
              className="opacity-50"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Select value={birthMonth} onValueChange={handleMonthChange}>
                <SelectTrigger className="w-full input-select">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent className="z-[2000]">
                  <SelectGroup>
                    {getMonths().map((month) => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={birthDay} onValueChange={handleDayChange}>
                <SelectTrigger className="w-full input-select">
                  <SelectValue placeholder="Day" />
                </SelectTrigger>
                <SelectContent className="z-[2000]">
                  <SelectGroup>
                    {daysInMonth.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={birthYear} onValueChange={handleYearChange}>
                <SelectTrigger className="w-full input-select">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent className="z-[2000]">
                  <SelectGroup>
                    {getYears().map((year) => (
                      <SelectItem key={year.value} value={year.value}>
                        {year.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        {/* Age */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium">Age</label>
            <ActorPrivacy 
              privacy={initialData.about_bio_age?.privacy || privacy}
              settingKey="about_bio_age"
              onPrivacyChange={handlePrivacyChange}
              actorType="user"
              editMode={true}
              miniIcon={true}
              viewerRelation={'self'}
            />
          </div>
          <span className="w-full input-select justify-start text-left px-3 py-1 rounded-full font-normal">
            {getAge()}
          </span>
        </div>

        {/* Gender */}
        <div className="space-y-2">
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium">Gender</label>
            <ActorPrivacy 
              privacy={initialData.about_bio_gender?.privacy || privacy}
              settingKey="about_bio_gender"
              onPrivacyChange={handlePrivacyChange}
              editMode={true}
              actorType="user"
              miniIcon
              viewerRelation={'self'}
            />
          </div>
          <Select value={gender} onValueChange={handleGenderChange}>
            <SelectTrigger className="input-select w-full">
              <SelectValue placeholder="Select your gender" />
            </SelectTrigger>
            <SelectContent className="z-[2000]">
              <SelectGroup>
                {genderOptions.map((gender) => (
                  <SelectItem key={gender.value} value={gender.value}>
                    {gender.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
          <div className="space-y-2">
          <div className="flex justify-between items-center">
          <label className="block text-sm font-medium">Where do you live?</label>
            <ActorPrivacy 
              privacy={initialData.about_bio_location?.privacy || privacy}
              settingKey="about_bio_location"
              onPrivacyChange={handlePrivacyChange}
              editMode={true}
              actorType="user"
              miniIcon
              viewerRelation={'self'}
            />
          </div>
            <Select value={location} onValueChange={handleLocationChange}>
                <SelectTrigger className="input-select w-full">
                    <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="z-[2000]">
                    <SelectGroup>
                        {countries.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                                {country.label}
                            </SelectItem>
                        ))}
                        <SelectItem key="rather_not_say" value="rather_not_say">
                            Rather not say
                        </SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>

        {/* Religion */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium">Religion</label>
            <ActorPrivacy 
              privacy={initialData.about_bio_religion?.privacy || privacy}
              settingKey="about_bio_religion"
              onPrivacyChange={handlePrivacyChange}
              actorType="user"
              editMode={true}
              miniIcon
              viewerRelation={'self'}
            />
          </div>
          <DropdownMenu>
            <button className="input-select w-full justify-start text-left font-normal border-t-0">
              <DropdownMenuTrigger asChild>
                <button className={`input-select w-full justify-start text-left px-3 py-2 font-normal hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover data-[state=open]:bg-eoe-icon-hover dark:data-[state=open]:bg-eoe-dark-icon-hover border-r-0 data-[state=open]:border-l ${religion.length > 0 ? "rounded-bl-none rounded-br-none" : ""} data-[state=open]:rounded-t-none`}>
                  {Array.isArray(religion) && religion.length > 0 
                    ? `${religion.length} selected` 
                    : "Select religious identities"}
                </button>
              </DropdownMenuTrigger>
              {Array.isArray(religion) && religion.length > 0 && (
                <div className="m-2 flex flex-wrap gap-1 z-[2001]">
                  {religion.map(relValue => {
                    const rel = religionOptions.find(option => option.value === relValue);
                    return (
                      <span 
                        key={relValue} 
                        className="inline-flex items-center px-2 py-1.5 rounded-full text-xs font-medium bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover data-[state=open]:rounded-none"
                      >
                        {rel?.label || relValue}
                        <button 
                          className="h-4 w-4 p-0 ml-1" 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleReligionToggle(relValue);
                          }}
                        >
                          <X className="size-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </button>
            <DropdownMenuContent 
              className="z-[2000] min-w-[calc(var(--radix-dropdown-menu-trigger-width)+2px)] max-h-[300px] overflow-y-auto border bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover cursor-pointer data-[side=top]:rounded-b-none data-[side=bottom]:rounded-t-none">
              {religionOptions.map((rel) => (
                <DropdownMenuCheckboxItem
                  key={rel.value}
                  checked={Array.isArray(religion) && religion.includes(rel.value)}
                  onCheckedChange={() => handleReligionToggle(rel.value)}
                  className="input-select border-none rounded-none"
                >
                  {rel.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Education */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium">Education</label>
            <ActorPrivacy 
              privacy={initialData.about_bio_education?.privacy || privacy}
              settingKey="about_bio_education"
              onPrivacyChange={handlePrivacyChange}
              actorType="user"
              editMode={true}
              miniIcon
              viewerRelation={'self'}
            />
          </div>
          <Select value={education} onValueChange={handleEducationChange}>
            <SelectTrigger className="input-select w-full">
              <SelectValue placeholder="Select your education level" />
            </SelectTrigger>
            <SelectContent className="z-[2000]">
              <SelectGroup>
                {educationOptions.map((education) => (
                  <SelectItem key={education.value} value={education.value}>
                    {education.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        {/* Political Identities */}
        <div className="space-y-2 pb-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium">Political Identities</label>
            <ActorPrivacy 
              privacy={initialData.about_bio_political_identities?.privacy || privacy}
              settingKey="about_bio_political_identities"
              onPrivacyChange={handlePrivacyChange}
              actorType="user"
              editMode={true}
              miniIcon
              viewerRelation={'self'}
            />
          </div>
          <DropdownMenu>
            <button className="input-select w-full justify-start text-left font-normal border-t-0">
              <DropdownMenuTrigger asChild>
                <button className={`input-select w-full justify-start text-left px-3 py-2 font-normal hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover data-[state=open]:bg-eoe-icon-hover dark:data-[state=open]:bg-eoe-dark-icon-hover border-r-0 data-[state=open]:border-l ${politicalIdentities.length > 0 ? "rounded-bl-none rounded-br-none" : ""} data-[state=open]:rounded-t-none`}>
                  {Array.isArray(politicalIdentities) && politicalIdentities.length > 0 
                    ? `${politicalIdentities.length} selected` 
                    : "Select political identities"}
                </button>
              </DropdownMenuTrigger>
              {Array.isArray(politicalIdentities) && politicalIdentities.length > 0 && (
                <div className="m-2 flex flex-wrap gap-1">
                  {politicalIdentities.map(identityValue => {
                    const identity = politicalIdentityOptions.find(option => option.value === identityValue);
                    return (
                      <span 
                        key={identityValue} 
                        className="inline-flex items-center px-2 py-1.5 rounded-full text-xs font-medium bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover data-[state=open]:rounded-none"
                      >
                        {identity?.label || identityValue}
                        <button 
                          className="h-4 w-4 p-0 ml-1" 
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handlePoliticalIdentityToggle(identityValue);
                          }}
                        >
                          <X className="size-4" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </button>
            <DropdownMenuContent className="z-[2000] min-w-[calc(var(--radix-dropdown-menu-trigger-width)+2px)] max-h-[300px] overflow-y-auto border bg-eoe-bg-modal-hover dark:bg-eoe-dark-bg-modal-hover cursor-pointer data-[side=top]:rounded-b-none data-[side=bottom]:rounded-t-none">
              <DropdownMenuLabel>Political Identities</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {politicalIdentityOptions.map((identity) => (
                <DropdownMenuCheckboxItem
                  key={identity.value}
                  checked={Array.isArray(politicalIdentities) && politicalIdentities.includes(identity.value)}
                  onCheckedChange={() => handlePoliticalIdentityToggle(identity.value)}
                >
                  {identity.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </Modal>
  )
}

export default UserAboutBackgroundModal
